// /modules/:key — one buyer-facing template over the reviewed module catalogue and profiles.
// The detail page pairs each module's approved 3D identity artwork with code-native,
// feature-specific operating evidence. The artwork identifies the capability; the operating
// view explains it without pretending to be a live product screenshot. Unknown key → /modules.
import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Briefcase, Building2, Check, ClipboardList, ConciergeBell, GraduationCap,
  // India-first: Lucide's generic `Receipt` draws a $ glyph in its artwork, so the repo aliases the
  // rupee variant everywhere (Landing.js, InvoiceDetailPage.js). Same alias here.
  Landmark, Calculator, ReceiptIndianRupee as Receipt, ShieldCheck, Sparkles, UserRound, Users, Workflow,
} from "lucide-react";
import { MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { BrandCopy } from "../components/BrandWordmarkText";
import { MarketingMotion, useMarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import { Button } from "../components/ui/primitives";
import api from "../lib/api";
import { moduleByKey, relatedModules, ENTITLEMENT_FOR } from "./landingparts/moduleCatalog";
import Module3DIcon from "../components/marketing/Module3DIcon";
import { module3dAsset } from "../components/marketing/module3dAssets";
import { init as initAnalytics, track } from "../lib/analytics";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import ModuleDeepDive from "./landingparts/ModuleDeepDive";
import { curatedModuleFaqsFor, deepDiveForModule } from "./landingparts/moduleDeepDiveContent";
import { moduleProfileFor } from "./landingparts/moduleProfiles";
import { moduleNarrativeFor } from "./landingparts/modulePageNarrative";
import ModuleExampleShowcase from "./landingparts/ModuleExampleShowcase";
import { buyerGuidesForCapability } from "./landingparts/moduleBuyerGuides";
import { moduleSeoDescription } from "./landingparts/moduleSeo";
import { faqPageSchemaFor, moduleFaqsFor } from "./landingparts/marketingFaqSchema";
import { CTA_PRIMARY_LABEL, CTA_SECONDARY_LABEL } from "../components/marketing/navigation";

const EASE = [0.22, 1, 0.36, 1];
const VoiceAiraModuleExperience = React.lazy(() => import("./landingparts/VoiceAiraModuleExperience"));

const MODULE_EVALUATION_NOTES = {
  "online-classes": "Requires the school's external Zoom, Meet or Teams link; Vidyapeeth360 does not host or record the call.",
  "online-payment": "Requires the school's configured collection provider and verified callback evidence.",
  "payment-gateway": "Requires the school's own Razorpay or Cashfree account; the provider controls final settlement timing and deductions.",
  "biometric-attendance": "Requires compatible device middleware. The product exposes a token-protected student IN-punch bridge, not a bundled vendor SDK.",
  "qr-attendance": "Requires a compatible scan flow and authorised device; confirm the intended attendance population during setup.",
  "rfid-attendance": "Requires compatible card-reader middleware and authorised device configuration.",
  "sms-alerts": "Requires configured provider credentials, approved templates, consent and applicable DLT rules.",
  "whatsapp-alerts": "Requires a configured WhatsApp provider, Meta-approved templates and consent controls.",
  "email-notifications": "Requires configured email delivery; provider acceptance does not guarantee inbox placement.",
  "push-notifications": "Native push is part of the early-access app programme and remains provider/device dependent.",
  "parent-app": "Native app is in early access ahead of public app-store release; confirm the preview scope before rollout.",
  "student-app": "Native app is in early access ahead of public app-store release; confirm the preview scope before rollout.",
  "teacher-app": "Native app is in early access; HR self-service also depends on the HR & Payroll workspace, which is still rolling out school by school.",
  "branch-management": "Requires Group & Chain on a plan that includes it, and a confirmed multi-school governance model.",
};

function evaluationNoteFor(mod) {
  if (MODULE_EVALUATION_NOTES[mod.key]) return MODULE_EVALUATION_NOTES[mod.key];
  if (mod.badge === "Release") return "This capability is still rolling out school by school; ask us about availability before you plan it into your rollout.";
  if (mod.badge === "Device") return "Confirm compatible hardware, middleware, permissions and the supported event direction before rollout.";
  if (mod.badge === "Configured") return "Confirm provider credentials, templates, consent and delivery-status behaviour before rollout.";
  if (mod.badge === "Early") return "This capability is early access; confirm the current preview scope and distribution path.";
  return "Confirm plan scope, role permissions, data dependencies and any provider or migration prerequisites during the demo and proposal.";
}

// Persona-chip glyphs for recurring exact role names. Unknown labels use a neutral person glyph.
const ROLE_ICONS = {
  Teachers: GraduationCap,
  "Class teachers": GraduationCap,
  "Academic coordinators": ClipboardList,
  Coordinators: ClipboardList,
  Principals: Landmark,
  Owners: Briefcase,
  "Owners & administrators": Briefcase,
  "Admin staff": Building2,
  "Front-office team": ConciergeBell,
  Accountants: Calculator,
  "Fee-desk staff": Receipt,
  Parents: Users,
};

// Module-specific roles from moduleProfiles, never family-level persona guesses.
function WhoUsesIt({ roles }) {
  if (!roles?.length) return null;
  return (
    <ul className="mt-5 flex flex-wrap gap-2" data-testid="module-roles">
      {roles.map((role) => {
        const RoleIcon = ROLE_ICONS[role] || UserRound;
        return (
          <li
            key={role}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-canvas px-3.5 py-2 text-[13px] font-semibold text-ink-2"
          >
            <RoleIcon className="h-4 w-4 text-accent" aria-hidden="true" /> {role}
          </li>
        );
      })}
    </ul>
  );
}

const revealProps = (reduce, delay = 0) => ({
  initial: reduce ? false : { opacity: 0, transform: "translateY(12px)" },
  whileInView: reduce ? undefined : { opacity: 1, transform: "translateY(0px)" },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: reduce ? 0 : 0.26, ease: EASE, delay: reduce ? 0 : delay },
});

function ModuleBuyerBrief({ mod, narrative, profile, featureMeta, tierName, reduce }) {
  const prerequisites = narrative.beforeRollout?.prerequisites || [];
  const rolloutState = featureMeta?.readiness_gated
    ? "Still rolling out school by school"
    : tierName
      ? `Included from ${tierName}`
      : featureMeta
        ? "Plan-dependent capability"
        : `${mod.workspaceTitle || mod.groupTitle} catalogue capability`;

  return (
    <section className="border-y border-line bg-surface" data-testid="module-buyer-brief">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
        <div className="grid border-y border-line lg:grid-cols-2">
          <m.article {...revealProps(reduce)} className="py-8 lg:pr-12" data-testid="module-what-it-does">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-accent">Product scope</p>
            <h2 className="mt-2 font-heading text-[25px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]">What it does</h2>
            <p className="mt-4 text-[15px] leading-[1.78] text-ink-2"><BrandCopy>{narrative.whatItDoes}</BrandCopy></p>
            <div className="mt-6 border-l-2 border-strong pl-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">The school problem</p>
              <p className="mt-2 text-[13.5px] leading-relaxed text-ink-2"><BrandCopy>{narrative.problem}</BrandCopy></p>
            </div>
          </m.article>

          <m.article {...revealProps(reduce, 0.035)} className="border-t border-line py-8 lg:border-l lg:border-t-0 lg:pl-12" data-testid="module-school-help">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Day-to-day work</p>
            <h2 className="mt-2 font-heading text-[25px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]">How your school uses it day to day</h2>
            <p className="mt-4 text-[14.5px] leading-relaxed text-ink-2"><BrandCopy>{narrative.helpsManage.summary}</BrandCopy></p>
            <ol className="mt-6 border-t border-line" data-testid="module-capability-list">
              {narrative.helpsManage.points.map((point, index) => (
                <li key={point} className="grid grid-cols-[34px_minmax(0,1fr)] gap-3 border-b border-line py-3.5">
                  <span className="pt-0.5 text-[10px] font-bold tabular-nums text-ink-subtle" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                  <span className="text-[13.5px] leading-relaxed text-ink-2"><BrandCopy>{point}</BrandCopy></span>
                </li>
              ))}
            </ol>
          </m.article>
        </div>

        <m.article {...revealProps(reduce)} className="grid gap-5 border-b border-line py-9 lg:grid-cols-[0.55fr_1.45fr] lg:items-start" data-testid="module-school-gets">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Operational result</p>
            <h2 className="mt-2 font-heading text-[25px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]">What your school gets</h2>
          </div>
          <p className="max-w-3xl font-heading text-[20px] font-semibold leading-[1.45] text-ink sm:text-[24px]"><BrandCopy>{narrative.schoolGets}</BrandCopy></p>
        </m.article>

        <div className="grid border-b border-line lg:grid-cols-2">
          <m.article {...revealProps(reduce)} className="py-8 lg:pr-12" data-testid="module-who-uses">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Access and ownership</p>
            <h2 className="mt-2 font-heading text-[23px] font-bold leading-tight tracking-tight text-ink sm:text-[27px]">Who typically uses it</h2>
            <WhoUsesIt roles={narrative.users || profile.roles} />
            <p className="mt-5 max-w-xl text-[13px] leading-relaxed text-ink-2">What each person can actually see depends on the role you give them and which classes or records you assign.</p>
          </m.article>

          <m.article {...revealProps(reduce, 0.035)} className="border-t border-line py-8 lg:border-l lg:border-t-0 lg:pl-12" data-testid="module-rollout">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Availability and setup</p>
            <h2 className="mt-2 font-heading text-[23px] font-bold leading-tight tracking-tight text-ink sm:text-[27px]">What to confirm before rollout</h2>
            <p className="mt-4 inline-flex min-h-9 items-center gap-2 rounded-full border border-line bg-muted px-3 text-[12px] font-semibold text-ink-2">
              <ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" /> {rolloutState}
            </p>
            {featureMeta?.description ? <p className="mt-4 text-[13px] leading-relaxed text-ink-2"><BrandCopy>{featureMeta.description}</BrandCopy></p> : null}
            <ul className="mt-5 space-y-3 text-[13px] leading-relaxed text-ink-2">
              {prerequisites.map((item) => (
                <li key={item} className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /><BrandCopy>{item}</BrandCopy></li>
              ))}
              {featureMeta?.requirement ? <li className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /><BrandCopy>{featureMeta.requirement}</BrandCopy></li> : null}
              <li className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /><BrandCopy>{evaluationNoteFor(mod)}</BrandCopy></li>
            </ul>
          </m.article>
        </div>
      </div>
    </section>
  );
}

function ModuleIdentityArt({ mod, reduce }) {
  return (
    <m.figure
      className="relative isolate mt-6 grid min-h-[270px] place-items-center overflow-hidden rounded-[28px] border border-line bg-surface px-5 pb-5 pt-3 text-center shadow-[0_28px_70px_-54px_rgb(var(--vp-fg-default)/0.4)] lg:mt-0"
      data-testid="module-3d-identity"
      initial={reduce ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
      animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
      transition={{ duration: reduce ? 0 : 0.24, ease: EASE }}
    >
      <span className="pointer-events-none absolute inset-x-[12%] top-[4%] -z-10 h-[72%] rounded-full bg-primary/10" aria-hidden="true" />
      <Module3DIcon
        Icon={mod.Icon}
        src={module3dAsset(mod)}
        color={mod.workspaceColor || mod.groupColor}
        size="xl"
        motionDisabled={reduce}
        ai={mod.group === "aira"}
        label={`Feature-specific 3D illustration for ${mod.name}`}
        showSemanticBadge={false}
        className="!h-44 !w-44 sm:!h-48 sm:!w-48"
      />
      <figcaption className="relative -mt-2 max-w-[27rem] border-t border-line px-2 pt-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Feature identity</p>
        <p className="mt-1 text-[14px] font-semibold leading-snug text-ink">{mod.name}</p>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-subtle">An illustration, not a screenshot. The interactive example below shows what this capability actually does.</p>
      </figcaption>
    </m.figure>
  );
}

function WorkflowLink({ mod }) {
  const Icon = mod.Icon || Workflow;
  return (
    <li>
      <Link
        to={`/modules/${mod.key}`}
        className="module-related-link grid min-h-[88px] grid-cols-[42px_minmax(0,1fr)_20px] items-start gap-3 border-b border-line py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        data-testid={`related-${mod.key}`}
      >
        <span className="grid h-10 w-10 place-items-center rounded-xl border border-line bg-surface text-accent" aria-hidden="true"><Icon className="h-[18px] w-[18px]" /></span>
        <span className="min-w-0"><strong className="block text-[13.5px] font-semibold text-ink">{mod.name}</strong><span className="mt-1 block text-[12px] leading-relaxed text-ink-subtle">{mod.oneLiner}</span></span>
        <ArrowRight className="mt-2 h-4 w-4 text-ink-subtle" aria-hidden="true" />
      </Link>
    </li>
  );
}

function AiAvailability({ narrative }) {
  const ai = narrative.aiSupport;
  if (!ai) {
    return (
      <aside className="border-t border-line pt-7 lg:border-l lg:border-t-0 lg:pl-10" data-testid="module-ai-unavailable">
        <Workflow className="h-5 w-5 text-accent" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-[20px] font-bold text-ink">Automation boundary</h3>
        <p className="mt-3 text-[13.5px] leading-relaxed text-ink-2">This capability does not use AI. It runs on the records, roles and permissions shown above.</p>
      </aside>
    );
  }

  const deterministic = ai.mode === "deterministic";
  const heading = deterministic
    ? "How this insight is produced"
    : ai.mode === "dedicated"
      ? "AI available in this capability"
      : "AI assistance available here";
  return (
    <aside className="border-t border-line pt-7 lg:border-l lg:border-t-0 lg:pl-10" data-testid="module-ai-availability" data-ai-mode={ai.mode}>
      {deterministic ? <ShieldCheck className="h-5 w-5 text-accent" aria-hidden="true" /> : <Sparkles className="h-5 w-5 text-ai-ink" aria-hidden="true" />}
      <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">{ai.label}</p>
      <h2 className="mt-2 font-heading text-[22px] font-bold leading-tight text-ink sm:text-[26px]">{heading}</h2>
      <ul className="mt-5 space-y-3">
        {ai.points.map((point) => (
          <li key={point} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-ink-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" /><BrandCopy>{point}</BrandCopy></li>
        ))}
      </ul>
      <p className="mt-5 border-l-2 border-strong pl-4 text-[12.5px] leading-relaxed text-ink-2">
        {deterministic
          ? "The documented signal is computed from configured rules and recorded data; it is not a model decision."
          : "Aira can support only the work listed here. Authorised people remain responsible for reviewing and confirming material actions."}
      </p>
      {ai.requirements.length ? (
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-[0.13em] text-ink-subtle">Requirements</p>
          <ul className="mt-2 space-y-2 text-[12.5px] leading-relaxed text-ink-2">
            {ai.requirements.map((requirement) => <li key={requirement}><BrandCopy>{requirement}</BrandCopy></li>)}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

function ModuleWorkflowAvailability({ narrative, related, reduce }) {
  return (
    <section className="bg-canvas" aria-labelledby="module-workflows-h" data-testid="module-workflows">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
        <m.div {...revealProps(reduce)} className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)] lg:gap-12">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-accent">Connected operating context</p>
            <h2 id="module-workflows-h" className="mt-2 font-heading text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[31px]">Available workflows</h2>
            <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-ink-2">These are the documented neighbouring capabilities that school teams may review with this module. A related page does not imply that every hand-off is automatic or enabled in every plan.</p>
            <ul className="mt-6 grid gap-x-8 border-t border-line md:grid-cols-2">
              {related.map((item) => <WorkflowLink key={item.key} mod={item} />)}
            </ul>
            <Link to="/modules" className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-lg text-[13.5px] font-semibold text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60">
              Explore all modules <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
          <AiAvailability narrative={narrative} />
        </m.div>
      </div>
    </section>
  );
}

function ModuleFaqs({ faqs }) {
  return (
    <section className="border-y border-line bg-surface" aria-labelledby="module-faq-h" data-testid="module-faqs">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:py-16 lg:grid-cols-[minmax(230px,0.58fr)_minmax(0,1.42fr)] lg:gap-14">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-accent">Buyer questions</p>
          <h2 id="module-faq-h" className="mt-2 font-heading text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[31px]">Frequently asked questions</h2>
          <p className="mt-3 text-[13.5px] leading-relaxed text-ink-2">The questions school owners ask before they subscribe: what it does, who can see it, and what to set up first.</p>
        </div>
        <div className="border-t border-line">
          {faqs.map((faq) => (
            <details key={faq.q} className="group border-b border-line py-1">
              <summary className="grid min-h-14 cursor-pointer list-none grid-cols-[minmax(0,1fr)_28px] items-center gap-4 rounded-lg py-3 text-[14px] font-semibold text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 [&::-webkit-details-marker]:hidden">
                <span>{faq.q}</span><span className="grid h-7 w-7 place-items-center rounded-full border border-line text-accent" aria-hidden="true">+</span>
              </summary>
              <p className="max-w-3xl pb-5 pr-10 text-[13.5px] leading-[1.72] text-ink-2"><BrandCopy>{faq.a}</BrandCopy></p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// WP-10: renamed module slugs keep their old, already-indexed URLs working (301-equivalent
// client redirect to the new canonical page — never a silent bounce to the hub).
const LEGACY_MODULE_KEYS = { "fee-reminders": "automated-fee-reminders" };

function ModulePageContent() {
  const { key: rawKey } = useParams();
  const key = LEGACY_MODULE_KEYS[rawKey] || rawKey;
  const mod = moduleByKey[key];
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = Boolean(prefersReducedMotion || paused);
  const navigate = useNavigate();
  const [salesOpen, setSalesOpen] = useState(false);
  // "Included from the <tier> plan" — stated ONLY when the module maps to exactly one gateable
  // entitlement id (ENTITLEMENT_FOR) AND the LIVE plan_matrix names a non-trial plan carrying it.
  // Anything else (no mapping, fetch failure, matrix change) renders no tier line at all.
  const featureId = mod ? ENTITLEMENT_FOR[mod.key] : null;
  const [tierName, setTierName] = useState(null);
  const [featureMeta, setFeatureMeta] = useState(null);

  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    if (!mod) return undefined;
    window.scrollTo(0, 0);
    track("feature_page_viewed", {
      module: mod.key,
      source_group: mod.group,
      workspace: mod.workspace,
    });
    return undefined;
  }, [mod]);
  useEffect(() => {
    setTierName(null);
    setFeatureMeta(null);
    if (!featureId) return undefined;
    let alive = true;
    api.get("/public/marketing")
      .then(({ data }) => {
        if (!alive) return;
        const plans = data?.plan_matrix?.plans || [];
        const features = data?.plan_matrix?.features || [];
        const hit = plans.find((p) => p?.id !== "plan_trial" && p?.features?.[featureId]);
        const meta = features.find((feature) => feature?.id === featureId);
        if (hit?.name) setTierName(String(hit.name));
        if (meta) setFeatureMeta(meta);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [featureId]);

  if (!mod) return <Navigate to="/modules" replace />;
  if (rawKey !== key) return <Navigate to={`/modules/${key}`} replace />;

  const profile = moduleProfileFor(mod);
  // PGC-3 — every authored deep-dive chapter renders on ITS route. The fee-collection and
  // result-publishing chapters were previously excluded here "until split by route", which left
  // the two most commercially decisive stories rendering NOWHERE (no page imported the homepage
  // flagship components). The chapters are scoped to the route's named capability by their keys.
  const deepDiveContent = deepDiveForModule(mod);
  const GIcon = mod.WorkspaceIcon || mod.GroupIcon;
  const groupRows = relatedModules(mod, 4);
  const buyerGuides = buyerGuidesForCapability(mod.key);
  const heroSummary = mod.oneLiner;
  const narrative = moduleNarrativeFor(mod, profile);
  // The visible disclosures and FAQPage schema share this exact array. This prevents the search
  // graph from publishing a fifth/hidden answer or drifting from the current rollout boundary.
  // SEO-6: curated non-templated Q&As lead on the deepened routes; the four standard
  // disclosures follow. Rendered copy and FAQPage schema still share this exact array.
  const faqs = [...curatedModuleFaqsFor(mod.key), ...moduleFaqsFor({
    mod,
    narrative,
    related: groupRows,
    featureMeta,
    tierName,
    evaluationNote: evaluationNoteFor(mod),
  })];
  // WP-10: real sentences, not auto-stitched fragments. Title stays under 60 chars (drop the
  // qualifier before the brand when a long module name would blow the budget); the description
  // stays within the search-snippet budget and comes from the module's reviewed summary.
  const seoTitle = (() => {
    const full = `${mod.name} Software for Indian Schools | Vidyapeeth360`;
    if (full.length <= 60) return full;
    const mid = `${mod.name} Software | Vidyapeeth360`;
    return mid.length <= 60 ? mid : `${mod.name} | Vidyapeeth360`;
  })();
  const seoDescription = moduleSeoDescription(mod);
  const seoSchema = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "WebPage", name: mod.name, description: seoDescription, url: `${seoOrigin}/modules/${mod.key}` },
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Modules", item: `${seoOrigin}/modules` },
        { "@type": "ListItem", position: 2, name: mod.name, item: `${seoOrigin}/modules/${mod.key}` },
      ] },
      { "@type": "SoftwareApplication", name: `${mod.name} - Vidyapeeth360`, applicationCategory: "BusinessApplication", operatingSystem: "Web", description: seoDescription },
      faqPageSchemaFor(faqs),
    ].filter(Boolean),
  };
  const isVoiceAira = mod.key === "voice-aira";

  return (
      <div
        className="min-h-screen bg-canvas text-ink flex flex-col landing-page"
        data-testid="module-page"
        data-module-key={mod.key}
        data-module-workspace={mod.workspace}
      >
        <MarketingSeo title={seoTitle} description={seoDescription} path={`/modules/${mod.key}`} schema={seoSchema} />
        <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
        <ScrollProgressBar />
        <main className="flex-1">
          {/* Hero — the buyer promise and 3D identity lead into one large route-specific example.
              The example replaces the retired generic evidence panel instead of duplicating it. */}
          {isVoiceAira ? (
            <React.Suspense fallback={<div className="min-h-[52rem] border-b border-line bg-[#f8f5f0]" aria-hidden="true" />}>
              <VoiceAiraModuleExperience
                mod={mod}
                reduce={reduce}
                demoLabel={CTA_SECONDARY_LABEL}
                registerLabel={CTA_PRIMARY_LABEL}
                onDemo={() => {
                  track("demo_request", { placement: "module_page", module: mod.key });
                  setSalesOpen(true);
                }}
                onRegister={() => {
                  track("trial_cta_click", { placement: "module_page", module: mod.key });
                  navigate("/register");
                }}
              />
            </React.Suspense>
          ) : (
          <section className="mx-auto max-w-7xl px-5 pb-12 pt-10 sm:pt-14 xl:pb-16">
            <div className="grid gap-7 lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.58fr)] lg:items-end lg:gap-12">
              <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2.5">
                <Link
                  to="/modules"
                  className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 ring-primary"
                  data-testid="module-back"
                >
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All modules
                </Link>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] font-semibold text-ink-2">
                  <GIcon className="h-3.5 w-3.5 text-accent" aria-hidden="true" /> {mod.workspaceTitle || mod.groupTitle}
                </span>
                {narrative.aiSupport && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ai/10 px-3 py-1.5 text-[12px] font-bold text-ai-ink">
                    {narrative.aiSupport.mode === "deterministic" ? <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
                    {narrative.aiSupport.mode === "deterministic" ? "Deterministic insight" : narrative.aiSupport.label}
                  </span>
                )}
              </div>
              {/* WP-10: keyword + audience, never a bare noun ("Fee Collection" told a searcher
                  and a screen-reader user almost nothing). */}
              <h1 className="mt-4 font-heading text-3xl font-bold leading-[1.12] tracking-tight text-ink sm:text-[40px]">
                {mod.name}{" "}
                <span className="block text-[0.55em] font-semibold text-ink-2 mt-1">
                  {narrative.aiSupport?.mode === "dedicated" ? "governed AI for Indian schools" : "software for Indian schools"}
                </span>
              </h1>
              <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink-2"><BrandCopy>{heroSummary}</BrandCopy></p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button size="lg" data-testid="module-demo"
                  onClick={() => { track("demo_request", { placement: "module_page", module: mod.key }); setSalesOpen(true); }}>
                  {CTA_SECONDARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button size="lg" variant="outline" data-testid="module-register"
                  onClick={() => { track("trial_cta_click", { placement: "module_page", module: mod.key }); navigate("/register"); }}>
                  {CTA_PRIMARY_LABEL}
                </Button>
              </div>
              </div>
              <ModuleIdentityArt mod={mod} reduce={reduce} />
            </div>
            <div className="mt-9 sm:mt-11">
              <ModuleExampleShowcase
                mod={mod}
                narrative={narrative}
                profile={profile}
                reduce={Boolean(prefersReducedMotion)}
                paused={paused}
              />
            </div>
          </section>
          )}

          {profile ? (
            <ModuleBuyerBrief
              mod={mod}
              narrative={narrative}
              profile={profile}
              featureMeta={featureMeta}
              tierName={tierName}
              reduce={reduce}
            />
          ) : null}

          {/* Route-specific evidence chapters — every authored deep dive renders on its route. */}
          {deepDiveContent ? (
            <ModuleDeepDive
              key={mod.key}
              mod={mod}
              content={deepDiveContent}
              reduce={reduce}
            />
          ) : null}

          <ModuleWorkflowAvailability narrative={narrative} related={groupRows} reduce={reduce} />

          <ModuleFaqs faqs={faqs} />

          <section className="mx-auto max-w-6xl px-5 pb-4" aria-label="Related evaluation guides">
            {buyerGuides.length ? (
              <div className="grid gap-3 border-t border-line py-5 sm:grid-cols-[minmax(150px,0.38fr)_minmax(0,1.62fr)] sm:items-center" data-testid="module-buyer-guide-links">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">Related evaluation guides</p>
                <ul className="flex flex-wrap gap-2 sm:justify-end">
                  {buyerGuides.map((guide) => (
                    <li key={guide.key}>
                      <Link
                        to={guide.path}
                        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-2 text-[12.5px] font-semibold text-ink-2 transition-[border-color,color] hover:border-primary/35 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                        data-testid={`module-buyer-guide-${guide.key}`}
                      >
                        {guide.shortTitle || guide.title} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-3 border-y border-line py-5">
              <Link
                to={`/modules#${mod.workspace || mod.group}`}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg text-[13.5px] font-semibold text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                data-testid="module-context-group"
              >
                See the {mod.workspaceTitle || mod.groupTitle} workspace <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg text-[13.5px] font-semibold text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                data-testid="module-context-pricing"
              >
                Compare plans and commercial costs <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </section>

          {/* Close — contextual demo first, school registration second. */}
          <section className="mx-auto max-w-6xl px-5 pb-14 pt-10 sm:pb-16">
            <div className="grid gap-7 rounded-[28px] border border-line bg-surface p-7 shadow-[0_24px_70px_-48px_rgb(var(--vp-fg-default)/0.42)] sm:p-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:text-left">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-accent">See it in your school context</p>
                <h2 className="mt-2 max-w-2xl font-heading text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[32px]">
                Run {mod.name.toLowerCase()} the connected way.
                </h2>
                <p className="mt-3 max-w-xl leading-relaxed text-ink-2">See how this workflow fits your school's current records, roles, plan and rollout requirements.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Button size="lg" data-testid="module-close-demo"
                  onClick={() => { track("demo_request", { placement: "module_page_close", module: mod.key }); setSalesOpen(true); }}>
                  {CTA_SECONDARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button size="lg" variant="outline" data-testid="module-close-register"
                  onClick={() => { track("trial_cta_click", { placement: "module_page_close", module: mod.key }); navigate("/register"); }}>
                  {CTA_PRIMARY_LABEL}
                </Button>
              </div>
            </div>
          </section>
        </main>
        <SiteFooter />
        <ContactSalesModal
          open={salesOpen}
          onClose={() => setSalesOpen(false)}
          contextLabel={mod.name}
          placement="module_page"
        />
      </div>
  );
}

// Keep every route hook and custom animation decision below the shared motion provider so the
// visible Pause control stops the identity reveal, section transitions and smooth-scroll behaviour.
export default function ModulePage() {
  return <MarketingMotion><ModulePageContent /></MarketingMotion>;
}
